import React, { useState } from 'react';
import { Modal, Form, InputNumber, Input, Select, Switch, Button, Space, Typography, Divider, message } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { WorkOrderLineItem } from '../../types';

const { Text } = Typography;
const { TextArea } = Input;

export interface ActualHoursEntry {
  line_item_id: number;
  category: string;
  service_description: string;
  actual_hours: number;
  estimated_hours?: number;
  tech_notes?: string;
}

interface LineItemFormState {
  skipped: boolean;
  actual_hours: number;
  category: string;
  tech_notes: string;
}

interface Props {
  open: boolean;
  lineItems: WorkOrderLineItem[];
  onComplete: (entries: ActualHoursEntry[]) => void;
  onSkip: () => void;
  loading?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: 'brakes', label: 'Brakes' },
  { value: 'cooling', label: 'Cooling' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'engine', label: 'Engine' },
  { value: 'steering_suspension', label: 'Steering / Suspension' },
  { value: 'ac', label: 'A/C' },
  { value: 'preventative', label: 'Preventative' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'diagnostics', label: 'Diagnostics' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'exhaust', label: 'Exhaust' },
  { value: 'tires', label: 'Tires' },
  { value: 'other', label: 'Other' },
];

const ActualHoursModal: React.FC<Props> = ({ open, lineItems, onComplete, onSkip, loading }) => {
  const [formState, setFormState] = useState<Record<number, LineItemFormState>>(() => {
    const initial: Record<number, LineItemFormState> = {};
    lineItems.forEach((item) => {
      if (item.id !== undefined) {
        initial[item.id] = {
          skipped: false,
          actual_hours: item.labor_hours ?? 0,
          category: 'other',
          tech_notes: '',
        };
      }
    });
    return initial;
  });

  const handleFieldChange = (lineItemId: number, field: keyof LineItemFormState, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [lineItemId]: { ...prev[lineItemId], [field]: value },
    }));
  };

  const handleComplete = () => {
    const entries: ActualHoursEntry[] = [];
    for (const item of lineItems) {
      if (item.id === undefined) continue;
      const state = formState[item.id];
      if (!state || state.skipped) continue;

      if (state.actual_hours < 0 || state.actual_hours > 40) {
        message.error(`Hours must be between 0 and 40 for: ${item.concern}`);
        return;
      }

      entries.push({
        line_item_id: item.id,
        category: state.category,
        service_description: item.concern.substring(0, 200),
        actual_hours: state.actual_hours,
        estimated_hours: item.labor_hours ?? undefined,
        tech_notes: state.tech_notes || undefined,
      });
    }
    onComplete(entries);
  };

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Record Actual Hours</span>
        </Space>
      }
      open={open}
      onCancel={onSkip}
      footer={[
        <Button key="skip" onClick={onSkip} disabled={loading}>
          Mark Complete Without Recording
        </Button>,
        <Button key="complete" type="primary" onClick={handleComplete} loading={loading}>
          Complete Work Order
        </Button>,
      ]}
      width={640}
      closable
      maskClosable={false}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Recording actual hours helps improve future labor estimates. This step is optional.
      </Text>

      {lineItems.map((item, idx) => {
        if (item.id === undefined) return null;
        const state = formState[item.id] ?? {
          skipped: false,
          actual_hours: item.labor_hours ?? 0,
          category: 'other',
          tech_notes: '',
        };

        return (
          <div key={item.id}>
            {idx > 0 && <Divider style={{ margin: '16px 0' }} />}
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Text strong style={{ maxWidth: 420, display: 'block' }}>{item.concern}</Text>
                <Space size={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Skip this item</Text>
                  <Switch
                    size="small"
                    checked={state.skipped}
                    onChange={(v) => handleFieldChange(item.id!, 'skipped', v)}
                  />
                </Space>
              </Space>

              {!state.skipped && (
                <Space wrap size={12} style={{ width: '100%' }}>
                  <Form.Item label="Category" style={{ marginBottom: 0 }}>
                    <Select
                      value={state.category}
                      onChange={(v) => handleFieldChange(item.id!, 'category', v)}
                      options={CATEGORY_OPTIONS}
                      style={{ width: 180 }}
                      size="small"
                    />
                  </Form.Item>

                  <Form.Item
                    label={`Actual Hours${item.labor_hours ? ` (est. ${item.labor_hours}h)` : ''}`}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      value={state.actual_hours}
                      min={0}
                      max={40}
                      step={0.1}
                      precision={1}
                      style={{ width: 100 }}
                      size="small"
                      onChange={(v) => handleFieldChange(item.id!, 'actual_hours', v ?? 0)}
                    />
                  </Form.Item>

                  <Form.Item label="Tech Notes (optional)" style={{ marginBottom: 0, width: '100%' }}>
                    <TextArea
                      value={state.tech_notes}
                      maxLength={500}
                      rows={2}
                      placeholder="Optional notes about this job..."
                      onChange={(e) => handleFieldChange(item.id!, 'tech_notes', e.target.value)}
                      size="small"
                    />
                  </Form.Item>
                </Space>
              )}
            </Space>
          </div>
        );
      })}
    </Modal>
  );
};

export default ActualHoursModal;
